import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bills, Income, Receipts as ReceiptsApi } from "@/lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const CalendarView = () => {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [bills, setBills] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    Promise.all([Bills.list(), Income.list(), ReceiptsApi.list()])
      .then(([b, i, r]) => {
        if (!cancel) {
          setBills(b);
          setIncomes(i);
          setReceipts(r);
        }
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = new Date(first);
    // Monday-start grid
    const dow = (first.getDay() + 6) % 7;
    start.setDate(first.getDate() - dow);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    const push = (d, e) => {
      if (!d) return;
      (map[d] = map[d] || []).push(e);
    };
    bills.forEach((b) =>
      push(b.due_date, {
        kind: "bill",
        url: `/bills/${b.id}`,
        title: b.company,
        amount: b.remaining_balance ?? b.amount_due,
        color: b.status?.includes("overdue") ? "red" : "amber",
      }),
    );
    incomes.forEach((i) =>
      push(i.expected_date, {
        kind: "income",
        url: "/income",
        title: i.client_name,
        amount: i.amount,
        color: i.received ? "green" : "blue",
      }),
    );
    receipts.forEach((r) =>
      push(r.receipt_date, {
        kind: "receipt",
        url: `/receipts/${r.id}`,
        title: r.vendor,
        amount: r.total_amount,
        color: "neutral",
      }),
    );
    return map;
  }, [bills, incomes, receipts]);

  const monthLabel = cursor.toLocaleString("en-AU", {
    month: "long",
    year: "numeric",
  });
  const today = iso(new Date());

  return (
    <div data-testid="calendar-view" className="fade-rise">
      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <CalendarIcon className="h-3 w-3" /> Calendar
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
          {monthLabel}
        </h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="cal-today"
            onClick={() => {
              const d = new Date();
              setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
            }}
            className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-3 py-2 hover:bg-black hover:text-white transition-colors"
          >
            Today
          </button>
          <button
            data-testid="cal-next"
            onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="h-10 w-10 border-2 border-neutral-300 hover:border-black text-neutral-700 hover:text-black flex items-center justify-center"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Every bill due, income expected, and receipt logged — on a single
        month view. Click any dot to open the record.
      </p>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-3 text-[11px] uppercase tracking-widest font-bold">
        <span className="inline-flex items-center gap-1.5"><Dot color="amber" /> Bill due</span>
        <span className="inline-flex items-center gap-1.5"><Dot color="red" /> Overdue</span>
        <span className="inline-flex items-center gap-1.5"><Dot color="blue" /> Income expected</span>
        <span className="inline-flex items-center gap-1.5"><Dot color="green" /> Income received</span>
        <span className="inline-flex items-center gap-1.5"><Dot color="neutral" /> Receipt</span>
      </div>

      {/* Day-of-week header */}
      <div className="mt-6 grid grid-cols-7 border-2 border-black bg-black text-white">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-widest font-bold text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l-2 border-r-2 border-b-2 border-black" data-testid="cal-grid">
        {days.map((d) => {
          const key = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const evs = eventsByDay[key] || [];
          const isToday = key === today;
          return (
            <div
              key={key}
              data-testid={`cal-day-${key}`}
              className={`min-h-[110px] border-t border-l border-neutral-200 p-1.5 ${
                inMonth ? "bg-white" : "bg-neutral-50 opacity-60"
              } ${isToday ? "ring-2 ring-black" : ""}`}
            >
              <div className={`text-[11px] font-bold ${isToday ? "text-red-600" : "text-neutral-700"}`}>
                {d.getDate()}
              </div>
              <div className="mt-1 space-y-0.5">
                {evs.slice(0, 4).map((e, i) => (
                  <Link
                    key={i}
                    to={e.url}
                    className="block truncate text-[10px] leading-tight hover:underline"
                    title={`${e.title} — ${fmt(e.amount)}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      <Dot color={e.color} />
                      <span className="truncate">{e.title}</span>
                    </span>
                  </Link>
                ))}
                {evs.length > 4 && (
                  <div className="text-[10px] text-neutral-500">
                    +{evs.length - 4} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {loading && (
        <div className="mt-4 text-xs text-neutral-500">Loading…</div>
      )}
    </div>
  );
};

const COLORS = {
  amber: "bg-amber-500",
  red: "bg-red-600",
  blue: "bg-blue-600",
  green: "bg-green-600",
  neutral: "bg-neutral-500",
};
const Dot = ({ color }) => (
  <span className={`inline-block h-2 w-2 ${COLORS[color] || "bg-neutral-500"}`} />
);

export default CalendarView;
